// HKDSE 2022 Paper 1 Knowledge Graph Samples
// Organized by question types with systematic naming

export const hkdse2022Samples = {
  algebra: [
    {
      id: 'hkdse2022_algebra_01',
      problem: "化簡 $\\frac{(a^3 b^{-2})^4}{a^{-5} b^6}$，並以正指數表示答案。",
      problemType: "algebra_indices",
      difficulty: "intermediate",
      description: "Simplifying expressions with negative indices",
      graphData: {
        nodes: [
          {
            id: "negative_indices",
            name: "Negative Indices",
            type: "concepts",
            description: "Understanding negative indices and their properties",
            value: "a⁻ⁿ = 1/aⁿ",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "power_of_power",
            name: "Power of Power",
            type: "concepts",
            description: "When raising a power to another power, multiply the indices",
            value: "(aᵐ)ⁿ = aᵐⁿ",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "division_of_powers",
            name: "Division of Powers",
            type: "concepts",
            description: "When dividing powers with the same base, subtract the indices",
            value: "aᵐ ÷ aⁿ = aᵐ⁻ⁿ",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_simplify_numerator",
            name: "Step 1: Simplify Numerator",
            type: "steps",
            description: "Apply power of power rule to (a³b⁻²)⁴",
            value: "(a³b⁻²)⁴ = a¹²b⁻⁸",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_divide",
            name: "Step 2: Divide by Denominator",
            type: "steps",
            description: "Divide a¹²b⁻⁸ by a⁻⁵b⁶",
            value: "a¹²b⁻⁸ ÷ a⁻⁵b⁶ = a¹⁷b⁻¹⁴",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "step3_convert_negative",
            name: "Step 3: Convert to Positive Indices",
            type: "steps",
            description: "Convert negative indices to positive using reciprocal",
            value: "a¹⁷b⁻¹⁴ = a¹⁷/b¹⁴",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The simplified expression in positive indices",
            value: "a¹⁷/b¹⁴",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "negative_indices", to: "step3_convert_negative", type: "enables", strength: 10 },
          { from: "power_of_power", to: "step1_simplify_numerator", type: "enables", strength: 9 },
          { from: "division_of_powers", to: "step2_divide", type: "enables", strength: 9 },
          { from: "step1_simplify_numerator", to: "step2_divide", type: "leads to", strength: 10 },
          { from: "step2_divide", to: "step3_convert_negative", type: "leads to", strength: 10 },
          { from: "step3_convert_negative", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'hkdse2022_algebra_02',
      problem: "設 $x$ 及 $y$ 為兩數。$x$ 與 $y$ 之和為 $456$ 而 $7$ 與 $x$ 之積為 $y$。求 $x$。",
      problemType: "algebra_simultaneous_equations",
      difficulty: "intermediate",
      description: "Solving simultaneous equations",
      graphData: {
        nodes: [
          {
            id: "equation1",
            name: "Equation 1",
            type: "concepts",
            description: "Sum of x and y is 456",
            value: "x + y = 456",
            importance: 9,
            x: -150, y: -200, z: 0
          },
          {
            id: "equation2",
            name: "Equation 2",
            type: "concepts",
            description: "Product of 7 and x equals y",
            value: "7x = y",
            importance: 9,
            x: 150, y: -200, z: 0
          },
          {
            id: "step1_substitute",
            name: "Step 1: Substitute y",
            type: "steps",
            description: "Substitute y = 7x into first equation",
            value: "x + 7x = 456",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_simplify",
            name: "Step 2: Simplify",
            type: "steps",
            description: "Combine like terms",
            value: "8x = 456",
            importance: 8,
            x: 0, y: 100, z: -15
          },
          {
            id: "step3_solve",
            name: "Step 3: Solve for x",
            type: "steps",
            description: "Divide both sides by 8",
            value: "x = 57",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Value of x",
            value: "x = 57",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "equation1", to: "step1_substitute", type: "enables", strength: 9 },
          { from: "equation2", to: "step1_substitute", type: "enables", strength: 9 },
          { from: "step1_substitute", to: "step2_simplify", type: "leads to", strength: 9 },
          { from: "step2_simplify", to: "step3_solve", type: "leads to", strength: 10 },
          { from: "step3_solve", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  factorization: [
    {
      id: 'hkdse2022_factorization_01',
      problem: "因式分解 (a) $9c^2 - 6c + 1$，(b) $(4c + d)^2 - 9c^2 + 6c - 1$。",
      problemType: "factorization",
      difficulty: "intermediate",
      description: "Factorization of quadratic expressions",
      graphData: {
        nodes: [
          {
            id: "perfect_square",
            name: "Perfect Square",
            type: "concepts",
            description: "Recognizing perfect square trinomial",
            value: "a² - 2ab + b² = (a - b)²",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "part_a_expression",
            name: "Part (a): Expression",
            type: "concepts",
            description: "9c² - 6c + 1",
            value: "9c² - 6c + 1",
            importance: 8,
            x: -150, y: -100, z: 20
          },
          {
            id: "part_a_recognize_pattern",
            name: "Part (a): Recognize Pattern",
            type: "steps",
            description: "Identify as perfect square",
            value: "9c² - 6c + 1 = (3c)² - 2(3c)(1) + 1²",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "part_a_factor",
            name: "Part (a): Factor",
            type: "steps",
            description: "Apply perfect square formula",
            value: "(3c - 1)²",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "part_b_expression",
            name: "Part (b): Expression",
            type: "concepts",
            description: "(4c + d)² - 9c² + 6c - 1",
            value: "(4c + d)² - 9c² + 6c - 1",
            importance: 8,
            x: 0, y: 100, z: -15
          },
          {
            id: "part_b_expand",
            name: "Part (b): Expand",
            type: "steps",
            description: "Expand (4c + d)²",
            value: "16c² + 8cd + d² - 9c² + 6c - 1",
            importance: 8,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_b_collect_terms",
            name: "Part (b): Collect Terms",
            type: "steps",
            description: "Combine like terms",
            value: "7c² + 8cd + d² + 6c - 1",
            importance: 8,
            x: 0, y: 300, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "Both factored expressions",
            value: "(a) (3c - 1)² (b) 7c² + 8cd + d² + 6c - 1",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "perfect_square", to: "part_a_recognize_pattern", type: "enables", strength: 9 },
          { from: "part_a_expression", to: "part_a_recognize_pattern", type: "enables", strength: 8 },
          { from: "part_a_recognize_pattern", to: "part_a_factor", type: "leads to", strength: 10 },
          { from: "part_b_expression", to: "part_b_expand", type: "enables", strength: 8 },
          { from: "part_b_expand", to: "part_b_collect_terms", type: "leads to", strength: 8 },
          { from: "part_a_factor", to: "final_answers", type: "contributes to", strength: 10 },
          { from: "part_b_collect_terms", to: "final_answers", type: "contributes to", strength: 8 }
        ]
      }
    }
  ],
  
  word_problems: [
    {
      id: 'hkdse2022_word_problem_01',
      problem: "某風扇以其標價七折售出。售出該風扇後，盈利為 $\\$78$ 且盈利百分率為 $26\\%$。求該風扇的標價。",
      problemType: "word_problem_percentage_profit",
      difficulty: "intermediate",
      description: "Solving percentage and profit problems",
      graphData: {
        nodes: [
          {
            id: "cost_price",
            name: "Cost Price",
            type: "concepts",
            description: "Let cost price be x",
            value: "CP = x",
            importance: 8,
            x: -200, y: -200, z: 0
          },
          {
            id: "marked_price",
            name: "Marked Price",
            type: "concepts",
            description: "Marked price is unknown",
            value: "MP = unknown",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "selling_price",
            name: "Selling Price",
            type: "concepts",
            description: "Sold at 70% of marked price",
            value: "SP = 0.7 × MP",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "profit_amount",
            name: "Profit Amount",
            type: "concepts",
            description: "Profit is $78",
            value: "SP - CP = 78",
            importance: 10,
            x: 0, y: 0, z: 0
          },
          {
            id: "profit_percentage",
            name: "Profit Percentage",
            type: "concepts",
            description: "Profit percentage is 26%",
            value: "SP = 1.26 × CP",
            importance: 10,
            x: 0, y: 100, z: 0
          },
          {
            id: "step1_express_sp",
            name: "Step 1: Express SP",
            type: "steps",
            description: "SP = 0.7 × MP = 1.26 × CP",
            value: "0.7 × MP = 1.26 × CP",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "step2_find_cp",
            name: "Step 2: Find CP",
            type: "steps",
            description: "From profit amount",
            value: "CP = SP - 78 = 1.26CP - 78",
            importance: 9,
            x: 0, y: 300, z: 0
          },
          {
            id: "step3_solve_cp",
            name: "Step 3: Solve for CP",
            type: "steps",
            description: "Solve the equation",
            value: "0.26CP = 78 → CP = 300",
            importance: 10,
            x: 0, y: 400, z: 0
          },
          {
            id: "step4_find_mp",
            name: "Step 4: Find MP",
            type: "steps",
            description: "Calculate marked price",
            value: "MP = 1.26 × 300 ÷ 0.7 = 540",
            importance: 10,
            x: 0, y: 500, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Marked price of the fan",
            value: "$540",
            importance: 10,
            x: 0, y: 600, z: 0
          }
        ],
        links: [
          { from: "cost_price", to: "step1_express_sp", type: "enables", strength: 8 },
          { from: "marked_price", to: "step1_express_sp", type: "enables", strength: 9 },
          { from: "selling_price", to: "step1_express_sp", type: "enables", strength: 9 },
          { from: "profit_amount", to: "step2_find_cp", type: "enables", strength: 10 },
          { from: "profit_percentage", to: "step1_express_sp", type: "enables", strength: 10 },
          { from: "step1_express_sp", to: "step2_find_cp", type: "enables", strength: 9 },
          { from: "step2_find_cp", to: "step3_solve_cp", type: "leads to", strength: 10 },
          { from: "step3_solve_cp", to: "step4_find_mp", type: "enables", strength: 10 },
          { from: "step4_find_mp", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  inequalities: [
    {
      id: 'hkdse2022_inequalities_01',
      problem: "考慮複合不等式 $-2(3x + 2) > x + 10$ 或 $2x \\leq -8$ ............(*)。(a) 解 (*)。(b) 寫出滿足 (*) 的最大整數。",
      problemType: "inequalities_compound",
      difficulty: "intermediate",
      description: "Solving compound inequalities with OR condition",
      graphData: {
        nodes: [
          {
            id: "compound_inequalities",
            name: "Compound Inequalities",
            type: "concepts",
            description: "Two inequalities connected by OR",
            value: "Inequality 1 OR Inequality 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "inequality1",
            name: "Inequality 1",
            type: "concepts",
            description: "First inequality to solve",
            value: "-2(3x + 2) > x + 10",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "inequality2",
            name: "Inequality 2",
            type: "concepts",
            description: "Second inequality to solve",
            value: "2x ≤ -8",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_solve_ineq1",
            name: "Step 1: Solve Inequality 1",
            type: "steps",
            description: "Solve -2(3x + 2) > x + 10",
            value: "-6x - 4 > x + 10",
            importance: 9,
            x: -200, y: 0, z: 25
          },
          {
            id: "step1a_expand",
            name: "Step 1a: Expand",
            type: "steps",
            description: "Expand the left side",
            value: "-6x - 4 > x + 10",
            importance: 8,
            x: -200, y: 50, z: 0
          },
          {
            id: "step1b_collect_terms",
            name: "Step 1b: Collect Terms",
            type: "steps",
            description: "Collect like terms",
            value: "-7x > 14",
            importance: 8,
            x: -200, y: 100, z: 0
          },
          {
            id: "step1c_solve",
            name: "Step 1c: Solve for x",
            type: "steps",
            description: "Divide by -7 (reverse inequality)",
            value: "x < -2",
            importance: 9,
            x: -200, y: 150, z: 0
          },
          {
            id: "step2_solve_ineq2",
            name: "Step 2: Solve Inequality 2",
            type: "steps",
            description: "Solve 2x ≤ -8",
            value: "x ≤ -4",
            importance: 8,
            x: 200, y: 0, z: 25
          },
          {
            id: "step3_combine",
            name: "Step 3: Combine Solutions",
            type: "steps",
            description: "Find union of both solutions",
            value: "x < -2 OR x ≤ -4",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "part_b_max_integer",
            name: "Part (b): Maximum Integer",
            type: "steps",
            description: "Find largest integer satisfying the condition",
            value: "x < -2, so maximum integer is -3",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "Solution and maximum integer",
            value: "(a) x < -2 OR x ≤ -4 (b) -3",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "compound_inequalities", to: "inequality1", type: "includes", strength: 9 },
          { from: "compound_inequalities", to: "inequality2", type: "includes", strength: 9 },
          { from: "inequality1", to: "step1_solve_ineq1", type: "requires", strength: 9 },
          { from: "step1_solve_ineq1", to: "step1a_expand", type: "leads to", strength: 8 },
          { from: "step1a_expand", to: "step1b_collect_terms", type: "leads to", strength: 8 },
          { from: "step1b_collect_terms", to: "step1c_solve", type: "leads to", strength: 9 },
          { from: "inequality2", to: "step2_solve_ineq2", type: "requires", strength: 8 },
          { from: "step1c_solve", to: "step3_combine", type: "enables", strength: 9 },
          { from: "step2_solve_ineq2", to: "step3_combine", type: "enables", strength: 9 },
          { from: "step3_combine", to: "part_b_max_integer", type: "enables", strength: 9 },
          { from: "step3_combine", to: "final_answers", type: "contributes to", strength: 9 },
          { from: "part_b_max_integer", to: "final_answers", type: "contributes to", strength: 9 }
        ]
      }
    }
  ]
}; 